import * as React from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useState, useEffect } from "react";
import Box from "components/common/Box";
import Text from "components/common/Text";
import BlurWrapper from "components/common/BlurWrapper";
import LockedTableWrapper from "components/common/LockTableWrapper";
import { usePhcTheme } from "../PhcThemeContext";

/** STOCK column — name + symbol */
const StockCell = ({ row }) => {
  const { theme } = usePhcTheme();
  const name = row?.name || row?.stock_name || row?.symbol || "—";
  return (
    <Text
      sx={{
        fontSize: "16px",
        fontWeight: 500,
        color: theme.textPrimary,
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </Text>
  );
};

/** SECTOR column — pill chip */
const SectorCell = ({ row }) => {
  const { theme } = usePhcTheme();
  const sector = row?.sector || row?.sector_name || "";
  if (!sector) return <Text sx={{ color: theme.textMuted }}>—</Text>;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: "8px",
        py: "6px",
        borderRadius: "30px",
        bgcolor: theme.badgeBg,
        whiteSpace: "nowrap",
      }}
    >
      <Text
        sx={{
          fontWeight: 500,
          fontSize: "12px",
          color: theme.textPrimary,
          lineHeight: "normal",
          textTransform: "uppercase",
        }}
      >
        {sector}
      </Text>
    </Box>
  );
};

/** View column — heading + description text */
const ViewCell = ({ heading, description, shouldBlur }) => {
  const { theme } = usePhcTheme();
  return (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      filter: shouldBlur ? "blur(4px)" : "none",
    }}
  >
    {heading && (
      <Text
        sx={{
          fontWeight: 500,
          fontSize: "16px",
          color: theme.textPrimary,
          whiteSpace: "nowrap",
          lineHeight: "normal",
        }}
      >
        {heading}
      </Text>
    )}
    {description && (
      <Text
        sx={{
          fontWeight: 300,
          fontSize: "12px",
          color: theme.textMuted,
          lineHeight: 1.4,
        }}
      >
        {description}
      </Text>
    )}
  </Box>
  );
};

const getFundamentalHeading = (row) =>
  row?.fundamental_view_heading ||
  row?.fundamental_analysis ||
  row?.fundamental_view ||
  "";

const getFundamentalDesc = (row) =>
  row?.fundamental_view_description ||
  row?.fundamental_description ||
  row?.fundamental_rationale ||
  "";

const getTechnicalHeading = (row) =>
  row?.technical_view_heading ||
  row?.technical_analysis ||
  row?.technical_view ||
  "";

const getTechnicalDesc = (row) =>
  row?.technical_view_description ||
  row?.technical_description ||
  row?.technical_rationale ||
  "";

const MobileJarvisAnalytics = ({ rows, shouldShowOverlay, componentState }) => {
  const { theme } = usePhcTheme();
  const OverlayComponent =
    componentState === "blur" ? BlurWrapper : LockedTableWrapper;

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Text
        sx={{
          fontWeight: 700,
          fontSize: "24px",
          color: theme.textPrimary,
          lineHeight: "32px",
          mb: "12px",
        }}
      >
        Jarvis Analytics
      </Text>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          borderRadius: "12px",
          border: theme.mobileCardBorder,
          overflow: "hidden",
          bgcolor: theme.mobileCardBg,
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            background: "transparent",
            boxShadow: "none",
            borderRadius: 0,
            overflowX: "auto",
            overflowY: "hidden",
            position: "relative",
            "&::-webkit-scrollbar": {
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(148,163,184,0.55)",
              borderRadius: "999px",
            },
          }}
        >
          <MuiTable
            sx={{
              minWidth: 640,
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Stock",
                  "Sector",
                  "Fundamental View",
                  "Technical View",
                ].map((label, index) => (
                  <TableCell
                    key={label}
                    sx={{
                      position: index === 0 ? "sticky" : "static",
                      left: index === 0 ? 0 : "auto",
                      // Keep sticky Stock header above unlock overlay
                      zIndex: index === 0 ? 5 : 3,
                      minWidth: index === 0 ? 116 : index === 1 ? 88 : 144,
                      px: index === 0 ? "16px" : "14px",
                      py: "12px",
                      bgcolor: theme.tableHeaderBgAlt,
                      borderBottom: `0.935px solid ${theme.tableRowBorder}`,
                      borderRight:
                        index === 0 ? `1px solid ${theme.tableStickyBorder}` : "none",
                    }}
                  >
                    <Text
                      sx={{
                        fontWeight: 700,
                        fontSize: "12px",
                        color: theme.tableHeaderText,
                        lineHeight: "16px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </Text>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`mobile-row-${index}`}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      // Sticky Stock cells stay above unlock while scrolling
                      zIndex: 4,
                      minWidth: 116,
                      px: "16px",
                      py: "13px",
                      bgcolor: theme.tableRowBg,
                      borderBottom:
                        index === rows.length - 1
                          ? "none"
                          : `0.935px solid ${theme.tableRowBorder}`,
                      borderRight: `1px solid ${theme.tableStickyBorder}`,
                      verticalAlign: "top",
                    }}
                  >
                    <Text
                      sx={{
                        fontWeight: 400,
                        fontSize: "12px",
                        color: theme.textPrimary,
                        lineHeight: "14px",
                        textTransform: "uppercase",
                        wordBreak: "break-word",
                      }}
                    >
                      {row?.name || row?.stock_name || row?.symbol || "—"}
                    </Text>
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 88,
                      px: "14px",
                      py: "13px",
                      bgcolor: theme.tableRowBg,
                      borderBottom:
                        index === rows.length - 1
                          ? "none"
                          : `0.935px solid ${theme.tableRowBorder}`,
                      verticalAlign: "top",
                    }}
                  >
                    <Text
                      sx={{
                        fontWeight: 400,
                        fontSize: "14px",
                        color: theme.textPrimary,
                        lineHeight: "20px",
                      }}
                    >
                      {row?.sector || row?.sector_name || "—"}
                    </Text>
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 144,
                      px: "14px",
                      py: "13px",
                      bgcolor: theme.tableRowBg,
                      borderBottom:
                        index === rows.length - 1
                          ? "none"
                          : `0.935px solid ${theme.tableRowBorder}`,
                      verticalAlign: "top",
                    }}
                  >
                    <ViewCell
                      heading={getFundamentalHeading(row)}
                      description={getFundamentalDesc(row)}
                      shouldBlur={shouldShowOverlay}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      minWidth: 144,
                      px: "14px",
                      py: "13px",
                      bgcolor: theme.tableRowBg,
                      borderBottom:
                        index === rows.length - 1
                          ? "none"
                          : `0.935px solid ${theme.tableRowBorder}`,
                      verticalAlign: "top",
                    }}
                  >
                    <ViewCell
                      heading={getTechnicalHeading(row)}
                      description={getTechnicalDesc(row)}
                      shouldBlur={shouldShowOverlay}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>

        {/* Fixed to card viewport (not table scroll) — stays on blurred area */}
        {shouldShowOverlay && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              // Start after sticky Stock column (116px) so unlock never covers it
              left: "124px",
              right: "10px",
              transform: "translateY(-50%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: "148px",
                maxWidth: "100%",
                pointerEvents: "auto",
              }}
            >
              <OverlayComponent
                blur={componentState === "blur"}
                locked={componentState === "locked"}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const JarvisAnalyticsTable = ({ javistable = [], componentState }) => {
  const { theme } = usePhcTheme();
  const [rows, setRows] = useState([]);
  const classes = useStyles({
    tableHeaderSolid: theme.tableHeaderSolid || "rgba(0,126,240,0.2)",
    tableHeaderText: theme.tableHeaderText || "#ffffff",
    tableRowBorder: theme.tableRowBorder || "#393939",
  });
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    setRows(javistable || []);
  }, [javistable]);

  if (!Array.isArray(javistable)) return null;

  const shouldShowOverlay =
    componentState === "blur" || componentState === "locked";

  const OverlayComponent =
    componentState === "blur" ? BlurWrapper : LockedTableWrapper;

  if (isMobile) {
    return (
      <MobileJarvisAnalytics
        rows={rows}
        shouldShowOverlay={shouldShowOverlay}
        componentState={componentState}
      />
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        mt: "10px",
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.sectionBorder}`,
        borderRadius: "20px",
        boxShadow:
          "0px -6px 6px -1px rgba(0,0,0,0.1), 0px -8px 4px -2px rgba(0,0,0,0.1)",
        px: { xs: 2, md: "19px" },
        pt: { xs: 2.5, md: "42px" },
        pb: { xs: 2, md: "32px" },
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* Title row */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: { xs: 2.5, md: "32px" },
          gap: 2,
        }}
      >
        <Text
          sx={{
            fontWeight: 700,
            fontSize: { xs: "20px", md: "24px" },
            color: theme.textPrimary,
            letterSpacing: "-0.72px",
            lineHeight: "none",
            whiteSpace: "nowrap",
          }}
        >
          Jarvis Analytics
        </Text>
      </Box>

      {/* Table */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          borderRadius: "20px",
          border: `1px solid ${theme.sectionBorder}`,
          backgroundImage: theme.sectionBg,
          backgroundColor: theme.cardBg,
          boxShadow: theme.cardShadow,
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            boxShadow: theme.innerPanelInsetGlow,
          },
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            background: "transparent",
            boxShadow: "none",
            borderRadius: 0,
            position: "relative",
          }}
        >
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  {["STOCK", "SECTOR", "FUNDAMENTAL VIEW", "TECHNICAL VIEW"].map(
                    (label) => (
                      <TableCell key={label} className={classes.header}>
                        {label}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell className={classes.bodyCell}>
                        <StockCell row={row} />
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        <SectorCell row={row} />
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        <ViewCell
                          heading={getFundamentalHeading(row)}
                          description={getFundamentalDesc(row)}
                          shouldBlur={shouldShowOverlay}
                        />
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        <ViewCell
                          heading={getTechnicalHeading(row)}
                          description={getTechnicalDesc(row)}
                          shouldBlur={shouldShowOverlay}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </Box>

          {shouldShowOverlay && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                // Center over blurred columns (Fundamental / Technical)
                left: { xs: "62%", md: "68%" },
                transform: "translate(-50%, -50%)",
                width: { xs: "135px", md: "250px" },
                height: "auto",
                zIndex: 2,
                pointerEvents: "auto",
              }}
            >
              <OverlayComponent
                blur={componentState === "blur"}
                locked={componentState === "locked"}
              />
            </Box>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
};

export default JarvisAnalyticsTable;

const useStyles = makeStyles({
  header: (props) => ({
    backgroundColor: `${props.tableHeaderSolid} !important`,
    color: `${props.tableHeaderText} !important`,
    fontWeight: "500 !important",
    fontSize: "18px !important",
    whiteSpace: "nowrap",
    borderBottom: "none !important",
    padding: "14px 16px !important",
  }),
  bodyCell: (props) => ({
    padding: "16px !important",
    borderBottom: `1px solid ${props.tableRowBorder} !important`,
    color: "inherit !important",
    verticalAlign: "top !important",
    backgroundColor: "transparent !important",
  }),
});
